import { useEffect, useState } from "react";
import { MoreVertical } from "lucide-react";
import styles from "../../../styles/patient-page-modules/Notes.module.css";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deletePatientNote } from "../../../services/patient/details";
import Spinner from "../../spinner";
import { convertUTCToLocal } from "../../../utils/helper";
import toast from "react-hot-toast";
import { Dropdown, DropdownItem } from "../../ui/dropdown";
import EditNoteDialog from "./edit-note";
import AlertDialog from "../../alert-dialogue";
import Button from "../../button-v1";
import { parseDateSafe } from "../../../utils/dateFormatters";

/**
 * Maps a note's `note_type` to a short label rendered inline next to
 * the author. Falls back to a generic "Note" label if the type is
 * missing or unrecognised. */
const programLabel = (noteType) => {
  switch (noteType) {
    case "rpm_note":
      return "RPM";
    case "ccm_note":
      return "CCM";
    case "careplan_note":
      return "Careplan";
    default:
      return "Note";
  }
};

/**
 * Decides which note shows the "active" navy dot vs the "inactive"
 * gray dot. Navy dot = notes that match the page-level RPM/CCM tab.
 * Careplan notes are always inactive (gray) since they aren't tied to
 * a program. */
const isActiveNote = (note, chatBotType) =>
  (chatBotType === "RPM" && note.note_type === "rpm_note") ||
  (chatBotType === "CCM" && note.note_type === "ccm_note");

const NotesList = ({
  notes,
  isLoading,
  error,
  refetch,
  onNoteClick,
  chatBotType,
  // Optional. When provided (drawer view), hides rows whose program is
  // unchecked. Careplan notes are CCM-category — governed by the CCM
  // checkbox. When omitted (inline panel), the full list renders unchanged.
  programFilter,
}) => {
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const queryClient = useQueryClient();
  const [expandedNoteIds, setExpandedNoteIds] = useState([]);
  const [currentNote, setCurrentNote] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const deleteMutation = useMutation(
    (id) => deletePatientNote(id, locationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-notes"]);
        toast.success("Note deleted successfully");
      },
      onError: () => {
        toast.error("Could not delete note");
      },
    }
  );

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      setIsDeleteDialogOpen(false);
    }
  }, [deleteMutation.isSuccess]);

  const handleAction = (action, note) => {
    if (action === "edit") {
      setCurrentNote(note);
      setIsEditDialogOpen(true);
    } else if (action === "delete") {
      setCurrentNote(note);
      setIsDeleteDialogOpen(true);
    } else if (action === "view-careplan") {
      const date = parseDateSafe(note?.updated_at) || new Date();
      const month = date.getUTCMonth() + 1;
      const year = date.getUTCFullYear();
      window.open(
        `/patient/finalize-careplan?month=${month}&year=${year}`,
        "_blank"
      );
    }
  };

  const toggleNoteExpansion = (event, noteKey) => {
    event.stopPropagation();
    setExpandedNoteIds((prev) =>
      prev.includes(noteKey)
        ? prev.filter((id) => id !== noteKey)
        : [...prev, noteKey]
    );
  };

  if (isLoading || deleteMutation.isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <div className={styles.empty}>Could not load notes.</div>;
  }

  // Apply the optional program filter (drawer-only). Finalized careplan
  // notes are CCM-category, so the CCM checkbox governs them too.
  const visible = programFilter
    ? (notes || []).filter((n) => {
        if (n.note_type === "rpm_note") return programFilter.rpm;
        if (n.note_type === "ccm_note" || n.note_type === "careplan_note")
          return programFilter.ccm;
        return true;
      })
    : notes;

  if (!visible || visible.length === 0) {
    return <div className={styles.empty}>No Notes Available</div>;
  }

  return (
    <>
      <div className={styles.listShell}>
      <div className={styles.list}>
        {visible.map((note, index) => {
          const noteKey = note?.id ?? index;
          const isExpanded = expandedNoteIds.includes(noteKey);
          const fullText = note?.note_text ?? "";
          const isLong = fullText.length > 280;
          const displayText = isLong && !isExpanded
            ? `${fullText.slice(0, 280)}...`
            : fullText;
          const isActive = isActiveNote(note, chatBotType);
          const isCareplan = note?.note_type === "careplan_note";

          return (
            <div
              key={noteKey}
              className={`${styles.row} ${
                isCareplan ? styles.rowCareplan : ""
              }`}
            >
              <div className={styles.timeline}>
                <span
                  className={`${styles.dot} ${
                    isActive ? styles.dotActive : styles.dotInactive
                  }`}
                  aria-hidden="true"
                />
                <span className={styles.line} aria-hidden="true" />
              </div>

              <div className={styles.content}>
                <div className={styles.headerLine}>
                  <p className={styles.author}>
                    {note?.assigned_by_name ||
                      (isCareplan ? "Care Plan Summary" : "_")}
                  </p>
                  <span className={styles.programChip}>
                    ({programLabel(note?.note_type)})
                  </span>
                  <span className={styles.date}>
                    {convertUTCToLocal(note?.created_at)}
                  </span>

                  <span className={styles.menuWrap}>
                    <Dropdown
                      align="right"
                      onChange={(value) => handleAction(value, note)}
                      trigger={
                        <button
                          type="button"
                          className={styles.menuBtn}
                          aria-label="Note actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      {/* IMPORTANT: each DropdownItem must be a direct child
                          of Dropdown — wrapping in a Fragment breaks
                          Dropdown's React.Children.map onClick injection. */}
                      {isCareplan && (
                        <DropdownItem value="view-careplan">
                          View Careplan
                        </DropdownItem>
                      )}
                      {!isCareplan && (
                        <DropdownItem value="edit">Edit</DropdownItem>
                      )}
                      {!isCareplan && (
                        <DropdownItem value="delete">Delete</DropdownItem>
                      )}
                    </Dropdown>
                  </span>
                </div>

                <p
                  className={`${styles.body} ${
                    isActive ? styles.bodyActive : ""
                  }`}
                  onClick={() => onNoteClick && onNoteClick(fullText)}
                  style={onNoteClick ? { cursor: "pointer" } : undefined}
                >
                  {displayText}
                  {isLong && (
                    <button
                      type="button"
                      className={styles.showMore}
                      onClick={(e) => toggleNoteExpansion(e, noteKey)}
                    >
                      {isExpanded ? "Hide" : "show more"}
                    </button>
                  )}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      </div>

      <EditNoteDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        note={currentNote}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        setOpen={setIsDeleteDialogOpen}
        title="Delete Note"
        description="Are you sure you want to delete this note?"
      >
        <Button
          variant="normal"
          size="default"
          onClick={() => setIsDeleteDialogOpen(false)}
        >
          Cancel
        </Button>
        <Button
          loading={deleteMutation.isLoading}
          variant="danger"
          size="default"
          onClick={() => deleteMutation.mutate(currentNote.id)}
        >
          Delete
        </Button>
      </AlertDialog>
    </>
  );
};

export default NotesList;
